/**
 * What a lead row offers, wherever it is asked.
 *
 * Split out of `LeadRowMenu` so the three-dot button at the end of the row and
 * the right-click on the row itself draw the same list. Two lists would drift -
 * the first entry somebody adds to one of them is the moment they stop being the
 * same menu, and a row where the gesture you used decides what you can do is
 * worse than a row with one way in.
 *
 * The right-click version carries a heading and the phone entry; those are the
 * only differences, and both come from props rather than from a second copy.
 */
import { Link } from "react-router-dom";
import { Brain, Building2, Check, Copy, ExternalLink, Mail, Phone, PhoneOutgoing, Radar, Trash2, UserPlus } from "lucide-react";
import { cn } from "@wa/ui";
import { LEAD_STATUSES, type LeadStatus, type ScanLead } from "../../../lib/scanLeads";
import { useCallContext } from "../../telephony/CallProvider";
import { useAiTestCall } from "../../telephony/aiTestCall";
import { useCopyToast } from "../../ui";
import type { T } from "../site-scanner/types";

export function LeadRowItems({
  lead,
  canManage,
  t,
  close,
  head,
  onOpenScan,
  onOpenBrain,
  onStatus,
  onPushToCrm,
  onDismiss,
}: {
  lead: ScanLead;
  canManage: boolean;
  t: T;
  close: () => void;
  /** The right-click menu says which row it opened from; the row's own button
   *  sits ON that row and does not have to. */
  head?: boolean;
  onOpenScan: () => void;
  onOpenBrain: () => void;
  onStatus: (s: LeadStatus) => void;
  onPushToCrm: () => void;
  onDismiss: () => void;
}) {
  const copy = useCopyToast();
  const call = useCallContext();
  const aiCall = useAiTestCall();
  const inCrm = Boolean(lead.crmLeadId);
  const canCall = Boolean(call?.enabled) && Boolean(lead.phone);

  const act = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <>
      {head ? (
        <div className="crm-menu-head">
          <div className="crm-menu-t">{lead.companyName ?? lead.domain}</div>
          {/* Only when it adds something. A lead with no matched company would
              otherwise have its domain printed twice, one line under itself. */}
          {lead.companyName ? <div className="crm-menu-s">{lead.domain}</div> : null}
        </div>
      ) : null}

      {/* First, because it is what the row itself does. The menu repeats it for
          the same reason it repeats the register link: an operator who reached
          for the menu should not have to go back and guess which part of the
          row was the control. */}
      <button type="button" className="crm-mi" onClick={() => act(onOpenScan)}>
        <Radar className="size-4" />
        {t("leads.openScan")}
      </button>
      {/* Next to the scan on purpose: they are the two "look at this lead"
          entries, and the brain is the one that answers "so what do I say".
          Ungated - reading a stored run is `view:ariregister`, and the run
          button inside the card is what `manage:ariregister` guards. */}
      <button type="button" className="crm-mi" onClick={() => act(onOpenBrain)}>
        <Brain className="size-4" />
        {t("leads.openBrain")}
      </button>
      {/* Beside the brain, because the test call is that brain said out loud:
          the dialog shows what the AI would say before a test number rings. */}
      {aiCall.canCall ? (
        <button
          type="button"
          className="crm-mi"
          onClick={() => act(() => aiCall.open({ domain: lead.domain, regCode: lead.regCode, name: lead.companyName }))}
        >
          <PhoneOutgoing className="size-4" />
          {t("aiOutbound.testCall.menu")}
        </button>
      ) : null}
      <a className="crm-mi" href={lead.url} target="_blank" rel="noreferrer noopener" onClick={close}>
        <ExternalLink className="size-4" />
        {t("leads.openSite")}
      </a>
      <button type="button" className="crm-mi" onClick={() => act(() => copy(lead.domain))}>
        <Copy className="size-4" />
        {t("leads.copyDomain")}
      </button>
      {lead.email ? (
        <button type="button" className="crm-mi" onClick={() => act(() => copy(lead.email!))}>
          <Mail className="size-4" />
          {t("common.copyEmail")}
        </button>
      ) : null}
      {canCall ? (
        <button
          type="button"
          className="crm-mi"
          onClick={() =>
            act(() =>
              call?.startCall({
                phone: lead.phone!,
                companyName: lead.companyName ?? undefined,
                regCode: lead.regCode ?? undefined,
                leadDomain: lead.domain,
                source: "scan-leads",
              }),
            )
          }
        >
          <Phone className="size-4" />
          {t("common.call")}
        </button>
      ) : null}
      {/* The same destination the company name in the row carries. The menu
          repeats it because the name is only a link when the scanner matched a
          register row, and an operator who reached for the menu should not have
          to go back and discover which of the two it was. */}
      {lead.regCode ? (
        <>
          <Link className="crm-mi" to={`/ariregister/${lead.regCode}`} onClick={close}>
            <Building2 className="size-4" />
            {t("leads.openRegister")}
          </Link>
          <button type="button" className="crm-mi" onClick={() => act(() => copy(lead.regCode!))}>
            <Copy className="size-4" />
            {t("common.copyRegCode")}
          </button>
        </>
      ) : null}

      {canManage ? (
        <>
          <div className="crm-menu-sep" />
          <div className="crm-menu-head">
            <span className="crm-menu-t">{t("leads.col.status")}</span>
          </div>
          {LEAD_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={cn("crm-mi", s === lead.status && "is-on")}
              onClick={() => act(() => onStatus(s))}
            >
              <span className="flex-1">{t(`leads.status.${s}`)}</span>
              {s === lead.status ? <Check className="size-4" /> : null}
            </button>
          ))}

          <div className="crm-menu-sep" />
          {/* Already pushed: the entry stays visible and says so, rather than
              disappearing. A row that loses a control after you used it makes
              you wonder whether the action worked. */}
          <button type="button" className="crm-mi" disabled={inCrm} onClick={() => act(onPushToCrm)}>
            <UserPlus className="size-4" />
            {t(inCrm ? "leads.action.inCrm" : "leads.action.toCrm")}
          </button>

          <button type="button" className="crm-mi crm-mi-danger" onClick={() => act(onDismiss)}>
            <Trash2 className="size-4" />
            {t("leads.action.dismiss")}
          </button>
        </>
      ) : null}
    </>
  );
}
